/**
 * upload/UploadOverlayPages.tsx — the V3 skin's entry point: the copied Design pages.
 *
 * V3 renders the DESIGNER'S OWN PAGE FILES (`pages/*`, copied verbatim from
 * `~/FrontendFinal/Design/src/pages/*`) wired to live data, as opposed to V2's
 * hand-written re-interpretation of them (`UploadOverlayReskin.tsx`). Both use the same
 * design system and the same hook; they differ only in who wrote the page layout.
 *
 * ─── WHY THIS FILE EXISTS AT ALL: P1 IS INSIDE THE OVERLAY ─────────────────────────
 *
 * The design draws FIVE modal frames in one box. P1 ("Select one of your Automations")
 * is the first thing you see AFTER the overlay opens — the automation picker lives in
 * the same 1056x640 frame as the four numbered steps, and stepping from P1 to P2 swaps
 * the contents while the box stays still. That is the whole point of the persistent
 * frame in `pages/UploadWizard.tsx`.
 *
 * V2 cannot do that, and the difference is structural rather than cosmetic. Under V2
 * the pick happens BEFORE the overlay: `Share.tsx` renders `FlowPickerReskin` as its
 * own separate modal, and only once a flow is picked does it mount the overlay at all
 * (`{openFlow && <UploadOverlay …/>}`). So V2 shows the picker in one frame, unmounts
 * it, and mounts a second frame for the wizard — two boxes, not one.
 *
 * THE OBSTACLE. `useUploadOverlay(props)` takes `flow` as a REQUIRED prop and fetches
 * that flow's components on mount. There is no way to express "the overlay is open but
 * no automation has been chosen yet" through that hook: the state does not exist in it,
 * and adding it would mean changing journey logic, which this whole reskin is under
 * standing instructions not to touch.
 *
 * THE RESOLUTION, and why it needs a component of its own. This component owns the
 * chosen flow in its OWN state and splits the overlay in two along that state:
 *
 *   chosenFlow == null -> render the frame + P1 here (`SelectFrame` below). No hook is
 *                         constructed, because there is no flow to construct it for.
 *   chosenFlow != null -> render `<UploadOverlayPagesWizard flow={chosenFlow} …/>`,
 *                         which is the ONLY place the hook is called. It is a separate
 *                         component precisely so the hook is called unconditionally
 *                         within it — a hook cannot sit behind an `if` in one component,
 *                         and mounting a child when the flow arrives is the idiomatic
 *                         React way to start a hook's life later than its parent's.
 *
 * The frame is therefore rendered by whichever half is live, and both halves render the
 * SAME frame markup — `SelectFrame` here is `pages/UploadWizard.tsx`'s frame with its
 * step switch replaced by P1. The visible box does not change identity between the two
 * halves as far as the user is concerned: same classes, same position, same size.
 *
 * WHAT THIS COSTS, stated plainly rather than hidden: crossing from P1 to P2 remounts
 * the frame element (one component unmounts, the other mounts), so the P1 -> P2
 * transition is not the pure in-place content cross-fade that P2 -> P3 -> P4 -> P5 is.
 * Every later step keeps the design's stationary box. Making P1 -> P2 stationary too
 * would require the hook to accept a null flow, i.e. changing journey logic.
 *
 * WHAT THAT COST USED TO INCLUDE, AND NO LONGER DOES: a white flash. Each half also drew
 * its OWN scrim, so the remount left one painted frame with no scrim at all — the overlay
 * blinked out to the bright page beneath before the incoming scrim faded up from zero.
 * Chrome painted that frame; other engines coalesced it away. The scrim is now mounted
 * once by this component and spans the handoff (see the persistent-scrim note below), so
 * what remains is the box swapping against an unbroken scrim. The frame remount described
 * above is still real — it is just no longer visible as a flash.
 *
 * WHAT THIS FILE DOES: it holds the local frame state that lives OUTSIDE the per-flow hook —
 * which automation is chosen (P1 -> P2), and the one thing before P1: the single-draft
 * "continue or start new" gate. It adds no validation and no upload API call; the moment a flow
 * is chosen everything is `useUploadOverlay`, unchanged.
 *
 * THE GATE (single-draft model). There is only ever ONE draft — the latest unfinished upload,
 * whatever automation it was for (see draftStore.latestDraft / clearOtherDrafts). If one exists when
 * the overlay opens, the builder is asked "continue where you left off, or start new?" BEFORE the
 * picker, rather than being silently dropped into P1 with their work waiting behind a footer button.
 * Continue resumes that draft's flow; Start new goes to P1. This is caller-side frame logic, not
 * journey logic — the resume/reconcile itself is still the hook's, reached via the flow the gate picks.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { scrimIn } from './design/motion';
import {
  WizardStepContext,
  type WizardModalName,
  type WizardStepValue,
} from './design/wizardStepContext';
import { Button } from './design/Button';
import { UploadSelectPage } from './pages/UploadSelectPage';
import { UploadWizard } from './pages/UploadWizard';
import { ArtefactWizard } from './pages/ArtefactWizard';
import { useUploadOverlay, type UploadOverlayProps } from './useUploadOverlay';
import { clearDraft, latestDraft } from './draftStore';
import type { ArtefactUploadResult, CloudFlowSummary } from '../api';

/**
 * V3's props are the shared `UploadOverlayProps` plus the FLOWS LIST, because P1 is now
 * inside the overlay and P1's whole content is that list. `UploadOverlayProps` carries a
 * single `flow` — the one the caller opened for — which is all V1/V2 ever needed, since
 * their picking happened outside the overlay.
 *
 * ADDED, NOT CHANGED: the shared props type is untouched, so V1 and V2 are unaffected;
 * this is a V3-only extra that `Share.tsx` passes alongside. `flow` is still required and
 * still passed, and V3 uses it as the fallback described on `chosenFlow` below.
 */
export interface UploadOverlayPagesProps extends UploadOverlayProps {
  /** The builder's cloud flows — the rows P1 lists. */
  flows: CloudFlowSummary[];
  /**
   * P1's pick, reported back to the caller. The caller pre-detects the automation's connectors to
   * decide whether Requirements is mandatory, and under V3 the pick happens in here rather than
   * before the overlay opens — so the caller needs telling. Optional: it only affects the TONE of one
   * field and fails open, so V3 works without it.
   */
  onFlowChosen?: (flow: CloudFlowSummary) => void;
}

export function UploadOverlayPages({ flows, onFlowChosen, ...props }: UploadOverlayPagesProps) {
  /**
   * The automation the builder picked on P1, held HERE rather than in the hook (see the
   * header). `props.flow` is the flow `Share.tsx` opened the overlay for, which under
   * V3 is a DEFAULT rather than a decision: the overlay opens on P1 regardless, so the
   * initial value is null and the builder chooses even if a row was clicked outside.
   *
   * Starting from null is deliberate. Seeding it with `props.flow` would skip P1 — the
   * exact behaviour V3 exists to fix.
   */
  const [chosenFlow, setChosenFlow] = useState<CloudFlowSummary | null>(null);

  /**
   * Whether the chosen flow was opened to RESUME its draft (the gate's "Continue draft" or the P1
   * footer button) rather than to start fresh (a plain row pick). Passed to the wizard hook as
   * `autoResumeDraft`, so a "continue" mount restores the draft straight away instead of stopping to
   * re-ask "resume or start new?" — the gate already asked that. A plain pick leaves it false.
   */
  const [resumeIntent, setResumeIntent] = useState(false);

  /**
   * The user's ONE current draft, read once when the overlay opens (a draft is per-browser state
   * that does not change under us mid-open). Drives the pre-picker gate and P1's "changed my mind"
   * button. `draftFlow` is that draft resolved back to a row in the list — null when the draft's
   * automation is no longer among the builder's flows (deleted in Power Automate since), which the
   * gate treats as "nothing to continue" rather than dead-ending on a flow that cannot be opened.
   */
  const draft = useMemo(() => latestDraft(props.userId), [props.userId]);
  const draftFlow = useMemo(
    () => (draft ? (flows.find((f) => f.workflowId === draft.workflowId) ?? null) : null),
    [draft, flows],
  );

  /**
   * The single-draft "continue or start new?" gate, shown BEFORE the picker when a resumable draft
   * exists (and its flow is still in the list). 'ask' -> the gate; 'picker' -> P1. Once the builder
   * chooses, we never return to the gate — Start new and a resumed-then-backed-out draft both land on
   * the picker, from where the footer "Continue Draft" button is the way back into the draft.
   */
  const [gate, setGate] = useState<'ask' | 'picker'>(draftFlow ? 'ask' : 'picker');

  /**
   * The PARALLEL artefact flow (File / Prompt / Link), entered from P1's "Upload from device"
   * (UP-F04). It is a THIRD state alongside "P1" and "Dataverse wizard": an artefact upload has no
   * cloud automation, so it does not go through `chosenFlow` / useUploadOverlay at all — it drives the
   * separate useArtefactUpload hook inside ArtefactWizard. `artefactDone` holds the server result once
   * the tool is created, so the same overlay can show a success frame without tearing itself down.
   */
  const [artefactMode, setArtefactMode] = useState(false);
  const [artefactDone, setArtefactDone] = useState<ArtefactUploadResult | null>(null);

  /**
   * The workflow the builder last opened the wizard on — remembered even AFTER they back out to P1
   * (`chosenFlow` goes null then, and the wizard that owns the draft unmounts). P1's explicit "Cancel"
   * button uses this to DISCARD that autosaved draft: a builder who went in, then backed all the way
   * out and cancelled did not mean to leave a draft behind for the resume prompt. Only the Cancel
   * BUTTON discards — the ✕, Escape and backdrop-click still keep the draft (that is "save and close").
   */
  const lastWorkedWorkflowId = useRef<string | null>(null);

  /**
   * Commit to an automation: mount its wizard, remember it for a later "Cancel" discard, and tell the
   * caller so its connector pre-detect runs. The ONE place `lastWorkedWorkflowId` is written — both P1's
   * row pick and the gate/"changed my mind" Continue funnel through here, so the ref mutation is
   * co-located in a single hook rather than scattered across two closures (react-hooks/immutability).
   */
  const commitFlow = useCallback(
    (flow: CloudFlowSummary, opts?: { resume?: boolean }) => {
      setChosenFlow(flow);
      // A "continue" commit auto-resumes; a plain pick starts fresh. Set explicitly (not left to a
      // stale value) so the two entry points never cross-contaminate.
      setResumeIntent(opts?.resume === true);
      lastWorkedWorkflowId.current = flow.workflowId;
      onFlowChosen?.(flow);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onFlowChosen is a stable prop callback
    [],
  );

  /** Continue the one latest draft: open its flow and resume it directly (no second resume prompt). */
  const onContinueDraft = useCallback(() => {
    if (draftFlow) {
      commitFlow(draftFlow, { resume: true });
    } else {
      // The drafted flow has since gone from the list — there is nothing to open, so fall through to
      // the picker rather than dead-end. (The gate would not have shown in this case, but the footer
      // button guards the same way.)
      setGate('picker');
    }
  }, [draftFlow, commitFlow]);

  const onBackdropClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) props.onClose();
    },
    [props],
  );

  const { onClose, userId } = props;
  /** P1 "Cancel": drop the draft the builder started (if any), then dismiss. See lastWorkedWorkflowId. */
  const onCancelFromSelect = useCallback(() => {
    if (lastWorkedWorkflowId.current) {
      clearDraft(userId, lastWorkedWorkflowId.current);
    }
    onClose();
  }, [onClose, userId]);

  return (
    <>
      {/* THE PERSISTENT SCRIM — mounted HERE, by the component that owns `chosenFlow`,
          so it survives the P1 -> P2 handoff.

          THE BUG IT FIXES (Chrome-visible white flash on P1 -> P2). The two halves below
          are separate components rendering separate portals, and each USED TO DRAW ITS
          OWN scrim. Picking an automation unmounts one half and mounts the other in a
          single commit, so for one painted frame NEITHER scrim existed: the whole dark
          overlay blinked out, the bright page behind it showed through, and the incoming
          scrim then faded up from opacity 0 via `scrimIn`. Chrome paints that intermediate
          frame; other engines coalesced it, which is why this reproduced only in Chrome.
          It is a genuinely unpainted frame, not a compositing artefact.

          Hoisting it changes nothing about the frame remount the file header describes
          (lines 46-50) — the modal box still swaps identity, which still needs the hook to
          accept a null flow to fix, and that is still journey logic this pass will not
          touch. But the scrim never depended on the hook, so it can be continuous even
          while the box behind it is not. What was a full-viewport blink becomes a swap of
          the box alone, against an unbroken scrim.

          Its own enter/exit still animates, because THIS element mounts when the overlay
          opens and unmounts when it closes — which is exactly the scrim's intended life
          (see scrimIn: opacity only, never moves). The backdrop click that used to live on
          each half's copy lives here now, same guarded handler, same dismiss. */}
      {createPortal(
        <motion.div
          variants={scrimIn}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={onBackdropClick}
          // `data-overlay-scrim` so the frames' inert sweeps can SKIP it. Those sweeps
          // mark every body-sibling of the overlay root inert, to take the page chrome
          // out of the tab order and the a11y tree together. This scrim is now such a
          // sibling — it lives in its own portal — and `inert` also kills pointer
          // events, so without the exemption the sweep would silently disable backdrop
          // dismiss the moment either frame mounted. It carries no focusable content,
          // so exempting it costs the trap nothing.
          data-overlay-scrim
          className="fixed inset-0 z-40 bg-scrim"
        />,
        document.body,
      )}

      {/* FOUR STATES off the same scrim, in this order and for these reasons:
            1. the PARALLEL artefact flow (File/Prompt/Link, UP-F04) — a mode the builder explicitly
               entered from P1, so it outranks every automation state; it has no cloud automation and
               never touches `chosenFlow` or useUploadOverlay at all.
            2. the Dataverse wizard, once a flow is chosen. Mounts the hook for that flow (see the
               header for why it is a separate component rather than a conditional hook call).
            3. the pre-picker gate, when a resumable draft is waiting.
            4. P1, the picker.

          The artefact branch is FIRST rather than folded in beside the gate: entering it is a
          deliberate act, whereas the gate fires off found state, and a builder who just chose "Upload
          from device" should not be interrupted by a draft prompt about a cloud automation. */}
      {artefactMode ? (
        artefactDone ? (
          <ArtefactDoneFrame
            result={artefactDone}
            onClose={props.onClose}
            onBack={() => {
              // Back to P1 for another upload — clear the artefact state so the flow restarts clean.
              setArtefactMode(false);
              setArtefactDone(null);
            }}
          />
        ) : (
          <ArtefactWizard
            refData={props.refData}
            onClose={props.onClose}
            onDone={setArtefactDone}
            // Back out of the artefact flow to P1, mirroring the wizard's onBackToSelect.
            onBackToSelect={() => setArtefactMode(false)}
          />
        )
      ) : chosenFlow ? (
        <UploadOverlayPagesWizard
          {...props}
          flow={chosenFlow}
          // Continue-draft mounts restore the draft straight away; plain picks show the wizard fresh.
          // This is what removes the second, duplicate "resume or start new?" prompt (see the hook).
          autoResumeDraft={resumeIntent}
          // Back out of the wizard to P1 rather than closing the overlay: the design puts
          // both in one box, so "back from P2" means the previous FRAME, which is P1.
          onBackToSelect={() => setChosenFlow(null)}
        />
      ) : gate === 'ask' && draft ? (
        <PlainFrame
          message={`You have an unfinished upload${
            draft.sourceName ? ` of "${draft.sourceName}"` : ''
          }. Carry on where you left off, or start a new one.`}
          onClose={props.onClose}
          choices={[
            { label: 'Continue draft', onClick: onContinueDraft },
            { label: 'Start new', onClick: () => setGate('picker') },
          ]}
        />
      ) : (
        <SelectFrame
          props={props}
          flows={flows}
          // The footer "Continue Draft" is a "changed my mind" button: it resumes the one latest
          // draft regardless of which row is selected, so it is enabled whenever a draft exists.
          hasDraft={draftFlow != null}
          onContinueDraft={onContinueDraft}
          onCancel={onCancelFromSelect}
          // UP-F04: leave the cloud-automation journey entirely for the artefact flow (branch 1).
          onUploadFromDevice={() => setArtefactMode(true)}
          // Same commit path as the gate: mounts the wizard, remembers the flow for a later
          // "Cancel" discard, and notifies the caller. See commitFlow.
          onPick={commitFlow}
        />
      )}
    </>
  );
}

/**
 * The hook-driven half. A separate component so `useUploadOverlay` is called
 * unconditionally (hooks cannot be conditional) with a flow that is guaranteed present,
 * and so the hook's whole life — including its components fetch — begins when the flow
 * is chosen rather than when the overlay opens.
 *
 * This is the thin adapter the plan describes: build the Vm with the unchanged hook,
 * hand it to the copied frame. Nothing else.
 */
function UploadOverlayPagesWizard({
  onBackToSelect,
  ...props
}: UploadOverlayProps & { onBackToSelect: () => void }) {
  // V3 alone has a real P5 success PAGE (phase 'done' -> UploadPublishPage). Defer
  // the completion callback so a successful upload lands ON P5 and stays there; P5's
  // own "Finish" button then fires props.onUploaded (close + list banner). Without
  // this the hook fires onUploaded on success, Share.tsx closes the overlay in the
  // same tick, and P5 is torn down before it paints — the upload works but the
  // confirmation page is skipped. See `deferCompletion` in useUploadOverlay.
  const vm = useUploadOverlay({ ...props, deferCompletion: true });

  /**
   * FOUR PHASES THE DESIGN DOES NOT DRAW: 'loading', 'unavailable', 'resume' and
   * 'duplicates'. `pages/UploadWizard.tsx` handles only the drawn ones ('wizard' and
   * 'done') and its own note says the caller keeps the rest — this is that caller.
   *
   * NO FRAME IS INVENTED for them, but neither is a real decision swallowed. Each gets
   * the design's own box with the DECISION IT CARRIES, in the design's type, and nothing
   * more — no pills, no columns, no fabricated layout:
   *
   *   'loading'     — one line. The fetch is usually sub-second; a made-up spinner
   *                   design would be inventing a frame.
   *   'unavailable' — the backend's own message, plus a way back to P1. A dead end WITH
   *                   a way back, as the phase's own comment puts it.
   *   'resume'      — Resume / Start new. This is a genuine either-or (a saved draft
   *                   exists for this automation) and both handlers already exist on the
   *                   Vm; dropping it would silently discard the builder's saved work.
   *   'duplicates'  — Continue / Abandon. Soft by design: the builder decides.
   *
   * These four are the honest gap in the port: they are real states of this journey with
   * no drawn equivalent, so V3 renders their choices plainly rather than guessing at a
   * frame. When the frames exist, they replace this block.
   */
  const phase = vm.phase;
  if (phase.kind !== 'wizard' && phase.kind !== 'done') {
    if (phase.kind === 'loading') {
      return (
        <PlainFrame
          message="Reading the automation…"
          onClose={props.onClose}
          onBack={onBackToSelect}
        />
      );
    }
    if (phase.kind === 'unavailable') {
      return <PlainFrame message={phase.message} onClose={props.onClose} onBack={onBackToSelect} />;
    }
    if (phase.kind === 'resume') {
      return (
        <PlainFrame
          message="You have a saved draft for this automation. Carry on where you left off, or start again from scratch."
          onClose={props.onClose}
          onBack={onBackToSelect}
          choices={[
            { label: 'Resume draft', onClick: vm.onResume },
            { label: 'Start new', onClick: vm.onStartNew },
          ]}
        />
      );
    }
    return (
      <PlainFrame
        message={
          phase.sameFlow
            ? 'This automation has already been shared. You can carry on and share it again, or stop here.'
            : 'Something similar has already been shared. You can carry on, or stop here and check first.'
        }
        onClose={props.onClose}
        onBack={onBackToSelect}
        choices={[
          { label: 'Carry on', onClick: vm.onDuplicatesContinue },
          { label: 'Stop here', onClick: vm.onDuplicatesAbandon },
        ]}
      />
    );
  }

  // renderScrim={false}: UploadOverlayPages owns the one persistent scrim, so this frame
  // must not draw a second. See the persistent-scrim note there.
  return <UploadWizard vm={vm} onBackToSelect={onBackToSelect} renderScrim={false} />;
}

/**
 * The design's frame, rendered around P1. This is `pages/UploadWizard.tsx`'s frame with
 * its step switch replaced by `<UploadSelectPage/>`: the portal, the inert sweep, the Tab
 * cycle, the centring box and every className are the same lines, because the box the
 * builder sees must not change when P1 hands off to P2.
 *
 * The SCRIM is the one thing it no longer duplicates — that now lives in
 * `UploadOverlayPages`, mounted once so it spans the handoff instead of blinking out
 * mid-swap. Both halves of the frame are otherwise still the same markup.
 *
 * It is DUPLICATED rather than extracted, and that is a deliberate trade: extracting a
 * shared `<Frame>` would mean editing `pages/UploadWizard.tsx` — a verbatim copy of the
 * designer's file, which the standing instruction says to keep as the designer wrote it.
 * The duplication is confined to this file and marked, rather than smeared into the
 * copied page.
 */
function SelectFrame({
  props,
  flows,
  hasDraft,
  onContinueDraft,
  onPick,
  onUploadFromDevice,
  onCancel,
}: {
  props: UploadOverlayProps;
  flows: CloudFlowSummary[];
  /** A draft exists to continue — enables P1's "changed my mind" Continue Draft button. */
  hasDraft: boolean;
  /** Resume the one latest draft (independent of the selected row). */
  onContinueDraft: () => void;
  onPick: (flow: CloudFlowSummary) => void;
  /** UP-F04: enter the parallel artefact flow (File/Prompt/Link). See artefactMode above. */
  onUploadFromDevice: () => void;
  /**
   * The explicit "Cancel" button's dismiss — discards any draft the builder started (see
   * lastWorkedWorkflowId in UploadOverlayPages). DISTINCT from `onClose`: backdrop-click and Escape
   * still use plain `onClose` (they keep the draft), so only the labelled Cancel button discards.
   */
  onCancel: () => void;
}) {
  const { onClose } = props;
  const dialogRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [modalName, setModalName] = useState<WizardModalName>({});
  const stepValue = useMemo<WizardStepValue>(() => ({ setModalName }), [setModalName]);

  const onBackdropClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) onClose();
    },
    [onClose],
  );

  // The same single focus trap the wizard frame runs: inert siblings + a Tab cycle on
  // the stable frame, restoring focus on unmount.
  useEffect(() => {
    const dialog = dialogRef.current;
    const overlay = overlayRef.current;
    if (!dialog || !overlay) return;

    const siblings = overlay.parentElement
      ? ([...overlay.parentElement.children] as HTMLElement[]).filter(
          // The persistent scrim is a body-sibling too, and `inert` would kill its
          // clicks along with its focusability — see data-overlay-scrim.
          (el) => el !== overlay && !el.hasAttribute('data-overlay-scrim'),
        )
      : [];
    const restore = siblings.map((el) => [el, el.inert] as const);
    siblings.forEach((el) => {
      el.inert = true;
    });

    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialog.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [
        ...dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      // `noUncheckedIndexedAccess` — the length check above already proves both are
      // present; this is the type system catching up, not a behaviour change.
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      restore.forEach(([el, wasInert]) => {
        el.inert = wasInert;
      });
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    // `pointer-events-none` + `pointer-events-auto` on the centring box: this layer no
    // longer paints the scrim, so it must not swallow clicks meant for the persistent
    // scrim beneath it (which owns backdrop dismiss now). The centring box re-enables
    // them for itself, keeping its own guarded click-off working.
    <div ref={overlayRef} className="pointer-events-none fixed inset-0 z-50" data-overlay-root>
      {/* NO SCRIM HERE ANY MORE — it is mounted once by UploadOverlayPages so it
          survives the P1 -> P2 handoff instead of blinking out during it. See the
          persistent-scrim comment there. */}
      {/* The backdrop's click-to-dismiss is a pointer-only CONVENIENCE. Keyboard users dismiss with
          Escape, wired on the document (see onKeyDown effect), so dismissal is fully keyboard-operable
          without this element being focusable. A full-screen backdrop must NOT be a tab stop, so the
          key-events rule is disabled for this line rather than satisfied with fake interactivity. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        onClick={onBackdropClick}
        className="pointer-events-auto absolute inset-x-0 bottom-0 top-header flex items-center justify-center p-modal-x"
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={modalName.labelledBy}
          aria-label={modalName.labelledBy ? undefined : modalName.label}
          tabIndex={-1}
          className={[
            'relative flex w-full max-w-modal flex-col',
            'h-modal max-h-full',
            'rounded-lg border border-ink bg-surface',
          ].join(' ')}
        >
          <WizardStepContext.Provider value={stepValue}>
            <AnimatePresence mode="wait" initial={false}>
              <UploadSelectPage
                key="select"
                flows={flows}
                environmentId={props.environmentId}
                hasDraft={hasDraft}
                onContinueDraft={onContinueDraft}
                onPick={onPick}
                onUploadFromDevice={onUploadFromDevice}
                // The Cancel BUTTON discards the draft; backdrop/Escape (handled above via onClose)
                // keep it. So P1's own dismiss control is onCancel, not onClose.
                onClose={onCancel}
              />
            </AnimatePresence>
          </WizardStepContext.Provider>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * The frame with a plain sentence in it, for the four phases the design does not draw
 * (see UploadOverlayPagesWizard) and for the pre-picker gate. It reuses the SAME box classes so an
 * undrawn state still looks like the product rather than a bare div — but it invents no frame: no
 * pills, no columns, no controls beyond a way back and a way out.
 *
 * `onBack` is OPTIONAL: the between-frames phases sit after P1 and offer "Back to automations", but
 * the gate is the FIRST frame — there is nothing behind it, and its forward choice ("Start new") is a
 * button, not a back link — so it omits the back link and shows only Close.
 */
function PlainFrame({
  message,
  choices,
  onClose,
  onBack,
}: {
  message: string;
  /** The phase's own decision, where it has one (Resume/Start new, Continue/Abandon, gate). */
  choices?: { label: string; onClick: () => void }[];
  onClose: () => void;
  onBack?: () => void;
}) {
  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-50" data-overlay-root>
      {/* NO SCRIM HERE EITHER — UploadOverlayPages mounts the one persistent scrim.
          This mattered twice over. These undrawn phases sit BETWEEN P1 and P2 in the
          real flow (picking a flow starts the components fetch, so phase 'loading'
          renders here before P2 appears), so a scrim of its own added a second blink
          to the very handoff being fixed. And because this copy was STATIC — no
          `scrimIn`, no fade — it would now stack on top of the persistent one, two
          0.4-alpha layers reading as a sudden darkening. */}
      <div className="pointer-events-auto absolute inset-x-0 bottom-0 top-header flex items-center justify-center p-modal-x">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Upload"
          className={[
            'relative flex w-full max-w-modal flex-col items-center justify-center gap-7 p-modal-x text-center',
            'h-modal max-h-full',
            'rounded-lg border border-ink bg-surface',
          ].join(' ')}
        >
          <p className="max-w-xl text-sm font-light text-ink">{message}</p>
          {/* The phase's real choices get the design's own button, so a decision
              looks like one. */}
          {choices && choices.length > 0 ? (
            <div className="flex items-center gap-modal-footer">
              {choices.map((choice) => (
                <Button
                  key={choice.label}
                  variant="outline-square"
                  size="xs"
                  onClick={choice.onClick}
                >
                  {choice.label}
                </Button>
              ))}
            </div>
          ) : null}
          {/* A way out always; a way back to P1 only where there is a P1 behind this frame. */}
          <div className="flex items-center gap-modal-footer">
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                className="text-xs font-light text-ink underline underline-offset-2"
              >
                Back to automations
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="text-xs font-light text-ink underline underline-offset-2"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * The artefact flow's success frame. The Dataverse path has its own drawn P5 (UploadPublishPage), but
 * that page is bound to the Dataverse vm; the artefact flow is parallel, so it gets the same plain box
 * treatment the undrawn phases use, carrying the one thing that matters — which route the tool took,
 * read from the REAL status the route echoed (Pending under manual_review, Approved under
 * self_moderated). No frame is invented: same box classes, a message, and a way on.
 */
function ArtefactDoneFrame({
  result,
  onClose,
  onBack,
}: {
  result: ArtefactUploadResult;
  onClose: () => void;
  onBack: () => void;
}) {
  const heldForReview = result.publication_status === 'Pending';
  const publishedLive = result.publication_status === 'Approved';
  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-50" data-overlay-root>
      <div className="pointer-events-auto absolute inset-x-0 bottom-0 top-header flex items-center justify-center p-modal-x">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Tool shared"
          className={[
            'relative flex w-full max-w-modal flex-col items-center justify-center gap-7 p-modal-x text-center',
            'h-modal max-h-full',
            'rounded-lg border border-ink bg-surface',
          ].join(' ')}
        >
          <p className="text-xl font-light leading-message text-ink">
            <span className="block">“{result.name}” is on its way.</span>
            <span className="block">Thank you for sharing it!</span>
          </p>
          <p className="max-w-xl text-sm font-light text-ink/60">
            {heldForReview
              ? 'It is now with the reviewer queue. Once it is approved, it is published to the catalogue.'
              : publishedLive
                ? 'It is now published and visible in the catalogue.'
                : 'It has been saved.'}
          </p>
          <div className="flex items-center gap-modal-footer">
            <Button variant="outline-square" size="xs" onClick={onBack}>
              Share another
            </Button>
            <Button variant="outline-square" size="xs" onClick={onClose}>
              Finish
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
